import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert } from '@edx/paragon';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import classNames from 'classnames';

import messages from './SocialLinks.messages';

// Icons
import InstagramIcon from '../assets/instagram-icon.svg';
import FacebookIcon from '../assets/facebook-icon.svg';
import LinkedInIcon from '../assets/linkedin-icon.svg';

// Components
import FormControls from './elements/FormControls';
import EditableItemHeader from './elements/EditableItemHeader';
import EmptyContent from './elements/EmptyContent';
import SwitchContent from './elements/SwitchContent';

// Selectors
import { editableFormSelector } from '../data/selectors';

const platformDisplayInfo = {
  facebook: {
    icon: FacebookIcon,
    name: 'Facebook',
  },
  instagram: {
    icon: InstagramIcon,
    name: 'Instagram',
  },
  linkedin: {
    icon: LinkedInIcon,
    name: 'LinkedIn',
  },
};

const SocialLinks = ({
  formId,
  socialLinks,
  draftSocialLinksByPlatform,
  visibilitySocialLinks,
  editMode: initialEditMode,
  saveState,
  error,
  intl,
  changeData,
  changeHandler,
  submitHandler,
  closeHandler,
  openHandler,
  saveData,
  onSaveComplete,
}) => {
  const [editMode, setEditMode] = useState(changeData ? 'editing' : initialEditMode);

  useEffect(() => {
    if (changeData) {
      handleOpen();
    } else {
      handleClose();
    }
  }, [changeData]);

  useEffect(() => {
    if (saveData) {
      handleSubmit();
    }
  }, [saveData]);

  const handleChange = e => {
    const { name, value } = e.target;

    if (name !== 'visibilitySocialLinks') {
      changeHandler(
        'socialLinks',
        mergeWithDrafts({
          platform: name,
          socialLink: value,
        }),
      );
    } else {
      changeHandler(name, value);
    }
  };

  const handleSubmit = e => {
    // Проверяем, есть ли объект события и выполняем preventDefault, если он существует
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    submitHandler(formId);
    if (onSaveComplete) {
      onSaveComplete();
    }
  };

  const handleClose = () => {
    closeHandler(formId);
    setEditMode('static');
  };

  const handleOpen = () => {
    openHandler(formId);
    setEditMode('editing');
  };

  const mergeWithDrafts = newSocialLink => {
    const knownPlatforms = ['instagram', 'linkedin', 'facebook'];
    const updated = [];
    knownPlatforms.forEach(platform => {
      if (newSocialLink.platform === platform) {
        updated.push(newSocialLink);
      } else if (draftSocialLinksByPlatform[platform] !== undefined) {
        updated.push(draftSocialLinksByPlatform[platform]);
      }
    });
    return updated;
  };

  return (
    <SwitchContent
      expression={editMode}
      cases={{
        empty: (
          <>
            <EditableItemHeader
              content={intl.formatMessage(messages['profile.sociallinks.social.links'])}
            />
            <ul className="list-unstyled">
              {socialLinks.map(({ platform }) => (
                <EmptyListItem
                  key={platform}
                  onClick={handleOpen}
                  name={platformDisplayInfo[platform].name}
                />
              ))}
            </ul>
          </>
        ),
        static: (
          <div className="tw-flex tw-gap-10 md:tw-flex-col md:tw-gap-4 xl:tw-gap-8">
            <p className="tw-font-medium tw-text-[17px] tw-tracking-tighter md:tw-tracking-normal md:tw-text-right xl:tw-text-[22px] xl:!tw-text-center">
              {intl.formatMessage(messages['profile.sociallinks.title'])}
            </p>
            <ul className="list-unstyled tw-flex tw-gap-8 md:tw-gap-4 md:tw-justify-end xl:tw-justify-center xl:tw-flex-row xl:tw-gap-8">
              {socialLinks.map(({ platform, socialLink }) => (
                <StaticListItem
                  key={platform}
                  name={platformDisplayInfo[platform].name}
                  url={socialLink}
                  platform={platform}
                />
              ))}
            </ul>
          </div>
        ),
        editable: (
          <>
            <EditableItemHeader
              content={intl.formatMessage(messages['profile.sociallinks.social.links'])}
              showEditButton
              onClickEdit={handleOpen}
              showVisibility={visibilitySocialLinks !== null}
              visibility={visibilitySocialLinks}
            />
            <ul className="list-unstyled">
              {socialLinks.map(({ platform, socialLink }) => (
                <EditableListItem
                  key={platform}
                  platform={platform}
                  name={platformDisplayInfo[platform].name}
                  url={socialLink}
                  onClickEmptyContent={handleOpen}
                />
              ))}
            </ul>
          </>
        ),
        editing: (
          <div role="dialog" aria-labelledby="social-links-label">
            <form className="xl:tw-px-4" aria-labelledby="editing-form" onSubmit={handleSubmit}>
              <div id="social-error-feedback">
                {error !== null && (
                  <Alert variant="danger" dismissible={false} show>
                    {error}
                  </Alert>
                )}
              </div>
              <h4 className="tw-hidden tw-font-medium tw-mb-4 md:tw-block xl:tw-hidden">
                {intl.formatMessage(messages['profile.social.media'])}
              </h4>
              <ul className="list-unstyled">
                {socialLinks.map(({ platform, socialLink }) => (
                  <EditingListItem
                    key={platform}
                    name={platformDisplayInfo[platform].name}
                    platform={platform}
                    value={socialLink}
                    onChange={handleChange}
                    error={error}
                  />
                ))}
              </ul>
              <div className="tw-hidden">
                <FormControls
                  visibilityId="visibilitySocialLinks"
                  saveState={saveState}
                  visibility={visibilitySocialLinks}
                  cancelHandler={handleClose}
                  changeHandler={handleChange}
                  saveData={saveData} // Передаем состояние saveData в FormControls
                />
              </div>
            </form>
          </div>
        ),
      }}
    />
  );
};

SocialLinks.propTypes = {
  formId: PropTypes.string.isRequired,
  socialLinks: PropTypes.arrayOf(
    PropTypes.shape({
      platform: PropTypes.string,
      socialLink: PropTypes.string,
    }),
  ).isRequired,
  draftSocialLinksByPlatform: PropTypes.objectOf(
    PropTypes.shape({
      platform: PropTypes.string,
      socialLink: PropTypes.string,
    }),
  ),
  visibilitySocialLinks: PropTypes.oneOf(['private', 'all_users']),
  editMode: PropTypes.oneOf(['editing', 'editable', 'empty', 'static']),
  saveState: PropTypes.string,
  error: PropTypes.string,
  changeHandler: PropTypes.func.isRequired,
  submitHandler: PropTypes.func.isRequired,
  closeHandler: PropTypes.func.isRequired,
  openHandler: PropTypes.func.isRequired,
  changeData: PropTypes.bool,
  intl: intlShape.isRequired,
};

SocialLinks.defaultProps = {
  editMode: 'static',
  saveState: null,
  draftSocialLinksByPlatform: {},
  visibilitySocialLinks: 'private',
  error: null,
  changeData: false,
};

export default connect(editableFormSelector, {})(injectIntl(SocialLinks));

const SocialLink = ({ url, name, platform }) => (
  <a href={url} target="_blank" className="tw-block tw-w-full tw-h-full">
    <img
      src={platformDisplayInfo[platform].icon}
      alt={name}
      className="tw-inline-block tw-w-full tw-h-full"
    />
  </a>
);

SocialLink.propTypes = {
  url: PropTypes.string.isRequired,
  platform: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

const EditableListItem = ({ url, platform, onClickEmptyContent, name }) => {
  const linkDisplay = url ? (
    <SocialLink name={name} url={url} platform={platform} />
  ) : (
    <EmptyContent onClick={onClickEmptyContent}>Add {name}</EmptyContent>
  );

  return <li className="form-group">{linkDisplay}</li>;
};

EditableListItem.propTypes = {
  url: PropTypes.string,
  platform: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onClickEmptyContent: PropTypes.func,
};

EditableListItem.defaultProps = {
  url: null,
  onClickEmptyContent: null,
};

const EditingListItem = ({ platform, name, value, onChange, error }) => (
  <li className="form-group tw-mb-2 md:tw-mb-4 xl:tw-mb-2">
    <div className="tw-relative">
      <img
        src={platformDisplayInfo[platform].icon}
        alt={name}
        className="tw-absolute tw-left-[14px] tw-top-[50%] -tw-translate-y-1/2 tw-w-5 tw-h-5"
      />
      <div className="tw-h-[26px] tw-w-[1px] tw-bg-lightGray tw-absolute tw-left-[44px] tw-top-[50%] -tw-translate-y-1/2" />
      <input
        className={classNames('input-text tw-w-full tw-border-none', {
          'is-invalid': Boolean(error),
        })}
        type="text"
        id={`social-${platform}`}
        name={platform}
        value={value || ''}
        onChange={onChange}
        aria-describedby="social-error-feedback"
        placeholder={name}
      />
    </div>
  </li>
);

EditingListItem.propTypes = {
  platform: PropTypes.string.isRequired,
  value: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

EditingListItem.defaultProps = {
  value: null,
  error: null,
};

const EmptyListItem = ({ onClick, name }) => (
  <li className="mb-4">
    <EmptyContent onClick={onClick}>
      <FormattedMessage
        id="profile.sociallinks.add"
        defaultMessage="Add {network}"
        values={{
          network: name,
        }}
        description="{network} is the name of a social network such as Facebook or Instagram"
      />
    </EmptyContent>
  </li>
);

EmptyListItem.propTypes = {
  name: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

const StaticListItem = ({ name, url, platform }) => (
  <li className="tw-w-[34px] tw-h-[34px] xl:tw-w-10 xl:tw-h-10">
    {url ? (
      <SocialLink name={name} url={url} platform={platform} />
    ) : (
      <div className="tw-w-full tw-h-full tw-opacity-60">
        <img
          src={platformDisplayInfo[platform].icon}
          alt={name}
          className="tw-inline-block tw-w-full tw-h-full"
        />
      </div>
    )}
  </li>
);

StaticListItem.propTypes = {
  name: PropTypes.string.isRequired,
  url: PropTypes.string,
  platform: PropTypes.string.isRequired,
};

StaticListItem.defaultProps = {
  url: null,
};
